import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Download, MessageSquare, Plus, Settings } from "lucide-react";

import { useAuth } from "@/components/AuthProvider";
import { HubSupportTicketMessageThread } from "@/components/HubSupportTicketMessageThread";
import { QueueItemPresenceAvatars } from "@/components/QueueItemPresenceAvatars";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { FilterChip } from "@/components/ui/FilterChip";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { useHubToast } from "@/components/ui/HubToast";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { VoiceToTextMicButton } from "@/components/ui/VoiceToTextMicButton";
import { ApiError } from "@/lib/api";
import { useIntegratorMe } from "@/lib/me-query";
import {
  MANUFACTURING_SAFETY_REPORT_ASSIGNEES_QUERY_KEY,
  MANUFACTURING_SAFETY_REPORTS_QUERY_KEY,
  PERSONAL_SAFETY_REPORTS_QUERY_KEY,
  SAFETY_MEDICAL_ATTENTION_LABELS,
  SAFETY_MEDICAL_ATTENTION_LEVELS,
  SAFETY_REPORT_CATEGORIES,
  SAFETY_REPORT_CATEGORY_LABELS,
  SAFETY_REPORT_KIND_LABELS,
  SAFETY_REPORT_KINDS,
  SAFETY_REPORT_SEVERITIES,
  SAFETY_REPORT_SEVERITY_LABELS,
  SAFETY_REPORT_STATUS_LABELS,
  SAFETY_REPORT_STATUSES,
  SAFETY_WORK_AREA_OPTIONS,
  downloadSafetyReportsCsv,
  fetchManufacturingSafetyReportAssignees,
  fetchManufacturingSafetyReportDetail,
  fetchManufacturingSafetyReports,
  patchManufacturingSafetyReport,
  postManufacturingSafetyReportMessage,
  safetyReportKindTone,
  safetyReportMessagesAsHubThread,
  safetyReportSeverityTone,
  safetyReportStatusIsTerminal,
  safetyReportStatusTone,
  type PatchManufacturingSafetyReportBody,
  type SafetyMedicalAttentionLevel,
  type SafetyReportCategory,
  type SafetyReportKind,
  type SafetyReportRow,
  type SafetyReportSeverity,
  type SafetyReportStatus,
} from "@/lib/safetyReportsApi";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { useQueueItemPresence } from "@/lib/useQueueItemPresence";
import { EM_DASH, formatTimestamp } from "@/lib/utils";

const OPEN_STATUSES: SafetyReportStatus[] = ["open", "investigating", "corrective_action"];
const SAFETY_REPORTS_QUEUE_PATHNAME = "/manufacturing-management/safety-reports";

const PAGE_HELP =
  "Review safety concerns, near misses, and injury or incident reports submitted by staff. Assign an owner, record containment, root cause, and corrective actions, flag OSHA recordable incidents, and communicate with the reporter. Manage the notification recipient list under Manufacturing management, Settings.";

interface InvestigationDraft {
  status: SafetyReportStatus;
  severity: SafetyReportSeverity;
  category: SafetyReportCategory;
  medical_attention_level: SafetyMedicalAttentionLevel;
  osha_recordable: boolean;
  pending_feedback: boolean;
  containment_action: string;
  root_cause: string;
  corrective_action: string;
  resolution_note: string;
}

function draftFromReport(report: SafetyReportRow): InvestigationDraft {
  return {
    status: report.status,
    severity: report.severity,
    category: report.category,
    medical_attention_level: report.medical_attention_level,
    osha_recordable: report.osha_recordable === true,
    pending_feedback: report.pending_feedback,
    containment_action: report.containment_action ?? "",
    root_cause: report.root_cause ?? "",
    corrective_action: report.corrective_action ?? "",
    resolution_note: report.resolution_note ?? "",
  };
}

function draftsDiffer(left: InvestigationDraft | null, right: InvestigationDraft | null): boolean {
  if (left == null || right == null) return false;
  return (
    left.status !== right.status ||
    left.severity !== right.severity ||
    left.category !== right.category ||
    left.medical_attention_level !== right.medical_attention_level ||
    left.osha_recordable !== right.osha_recordable ||
    left.pending_feedback !== right.pending_feedback ||
    left.containment_action.trim() !== right.containment_action.trim() ||
    left.root_cause.trim() !== right.root_cause.trim() ||
    left.corrective_action.trim() !== right.corrective_action.trim() ||
    left.resolution_note.trim() !== right.resolution_note.trim()
  );
}

export function ManufacturingSafetyReportsPage(): JSX.Element {
  const canManage = useHubModuleWrite("manufacturing_management");
  const meQuery = useIntegratorMe();
  const { session } = useAuth();
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const currentUserId = meQuery.data?.user.id?.trim() || session?.user.id?.trim() || "";

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("report")?.trim() ?? "";

  const [statusScope, setStatusScope] = useState<"open" | "all">("open");
  const [selectedKinds, setSelectedKinds] = useState<SafetyReportKind[]>([]);
  const [selectedSeverities, setSelectedSeverities] = useState<SafetyReportSeverity[]>([]);
  const [workAreaFilter, setWorkAreaFilter] = useState<string>("");
  const [pendingFeedbackOnly, setPendingFeedbackOnly] = useState(false);
  const [oshaRecordableOnly, setOshaRecordableOnly] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 280);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const listQuery = useQuery({
    queryKey: [
      ...MANUFACTURING_SAFETY_REPORTS_QUERY_KEY,
      "list",
      statusScope,
      selectedKinds.join(","),
      selectedSeverities.join(","),
      workAreaFilter,
      pendingFeedbackOnly,
      oshaRecordableOnly,
      debouncedSearch,
    ],
    queryFn: () =>
      fetchManufacturingSafetyReports({
        status: statusScope === "open" ? OPEN_STATUSES.join(",") : undefined,
        reportKind: selectedKinds.length > 0 ? selectedKinds.join(",") : undefined,
        severity: selectedSeverities.length > 0 ? selectedSeverities.join(",") : undefined,
        workArea: workAreaFilter !== "" ? workAreaFilter : undefined,
        pendingFeedback: pendingFeedbackOnly,
        oshaRecordable: oshaRecordableOnly,
        search: debouncedSearch !== "" ? debouncedSearch : undefined,
        limit: 150,
      }),
  });

  const rows = listQuery.data?.rows ?? [];

  const { viewersForEntity } = useQueueItemPresence({
    pathname: SAFETY_REPORTS_QUEUE_PATHNAME,
    enabled: true,
  });

  const detailQuery = useQuery({
    queryKey: [...MANUFACTURING_SAFETY_REPORTS_QUERY_KEY, "detail", selectedId],
    queryFn: () => fetchManufacturingSafetyReportDetail(selectedId),
    enabled: selectedId !== "",
  });

  const assigneeOptionsQuery = useQuery({
    queryKey: MANUFACTURING_SAFETY_REPORT_ASSIGNEES_QUERY_KEY,
    queryFn: () => fetchManufacturingSafetyReportAssignees(),
    enabled: canManage && selectedId !== "",
  });

  const selectedListRow = useMemo(
    () => rows.find((row) => row.id === selectedId) ?? null,
    [rows, selectedId],
  );
  const selected = detailQuery.data?.report ?? selectedListRow;

  const [draft, setDraft] = useState<InvestigationDraft | null>(null);
  const [replyBody, setReplyBody] = useState("");
  const [requestFeedbackOnReply, setRequestFeedbackOnReply] = useState(false);

  useEffect(() => {
    if (!listQuery.isSuccess || rows.length === 0) {
      return;
    }
    const hasValidSelection = selectedId !== "" && rows.some((row) => row.id === selectedId);
    if (hasValidSelection || selectedId !== "") {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("report", rows[0]!.id);
    setSearchParams(next, { replace: true });
  }, [listQuery.isSuccess, rows, searchParams, selectedId, setSearchParams]);

  const loadedSnapshot = useMemo(
    () => (selected != null ? draftFromReport(selected) : null),
    [selected],
  );

  useEffect(() => {
    if (selected != null) {
      setDraft(draftFromReport(selected));
    } else {
      setDraft(null);
    }
  }, [
    selected?.id,
    selected?.updated_at,
  ]);

  const isDirty = useMemo(() => draftsDiffer(draft, loadedSnapshot), [draft, loadedSnapshot]);

  const patchMutation = useMutation({
    mutationFn: ({
      reportId,
      body,
    }: {
      reportId: string;
      body: PatchManufacturingSafetyReportBody;
    }) => patchManufacturingSafetyReport(reportId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MANUFACTURING_SAFETY_REPORTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PERSONAL_SAFETY_REPORTS_QUERY_KEY });
      showToast({ tone: "success", message: "Safety report updated." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const replyMutation = useMutation({
    mutationFn: ({ body, requestFeedback }: { body: string; requestFeedback: boolean }) =>
      postManufacturingSafetyReportMessage(selectedId, body, requestFeedback),
    onSuccess: () => {
      setReplyBody("");
      setRequestFeedbackOnReply(false);
      void queryClient.invalidateQueries({ queryKey: MANUFACTURING_SAFETY_REPORTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PERSONAL_SAFETY_REPORTS_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const summaryCounts = useMemo(() => {
    let openCount = 0;
    let incidentCount = 0;
    let highCriticalOpenCount = 0;
    let pendingFeedbackCount = 0;
    let oshaRecordableCount = 0;
    for (const row of rows) {
      const open = !safetyReportStatusIsTerminal(row.status);
      if (open) openCount += 1;
      if (row.report_kind === "incident" && open) incidentCount += 1;
      if (open && (row.severity === "high" || row.severity === "critical")) {
        highCriticalOpenCount += 1;
      }
      if (row.pending_feedback) pendingFeedbackCount += 1;
      if (row.osha_recordable) oshaRecordableCount += 1;
    }
    return {
      openCount,
      incidentCount,
      highCriticalOpenCount,
      pendingFeedbackCount,
      oshaRecordableCount,
    };
  }, [rows]);

  const assigneeOptions = useMemo(() => {
    const users = assigneeOptionsQuery.data?.users ?? [];
    const options = users.map((user) => ({
      userId: user.id,
      label: user.display_name?.trim() ? user.display_name : user.id,
    }));
    const currentAssigneeId = selected?.assigned_to_user_id?.trim() ?? "";
    if (
      currentAssigneeId !== "" &&
      !options.some((option) => option.userId === currentAssigneeId) &&
      selected?.assigned_to_display_name
    ) {
      options.unshift({
        userId: currentAssigneeId,
        label: selected.assigned_to_display_name,
      });
    }
    return options;
  }, [
    assigneeOptionsQuery.data?.users,
    selected?.assigned_to_display_name,
    selected?.assigned_to_user_id,
  ]);

  const isAssignedToCurrentUser =
    currentUserId !== "" &&
    selected?.assigned_to_user_id != null &&
    selected.assigned_to_user_id.toLowerCase() === currentUserId.toLowerCase();

  function toggleKindChip(kind: SafetyReportKind): void {
    setSelectedKinds((current) =>
      current.includes(kind) ? current.filter((item) => item !== kind) : [...current, kind],
    );
  }

  function toggleSeverityChip(severity: SafetyReportSeverity): void {
    setSelectedSeverities((current) =>
      current.includes(severity)
        ? current.filter((item) => item !== severity)
        : [...current, severity],
    );
  }

  function selectRow(row: SafetyReportRow): void {
    const next = new URLSearchParams(searchParams);
    next.set("report", row.id);
    setSearchParams(next, { replace: true });
  }

  function saveDraftChanges(overrideStatus?: SafetyReportStatus): void {
    if (selectedId === "" || draft == null) return;
    const statusToSave = overrideStatus ?? draft.status;
    patchMutation.mutate({
      reportId: selectedId,
      body: {
        status: statusToSave,
        severity: draft.severity,
        category: draft.category,
        medical_attention_level: draft.medical_attention_level,
        osha_recordable: draft.osha_recordable,
        pending_feedback: safetyReportStatusIsTerminal(statusToSave) ? false : draft.pending_feedback,
        containment_action: draft.containment_action.trim() || null,
        root_cause: draft.root_cause.trim() || null,
        corrective_action: draft.corrective_action.trim() || null,
        resolution_note: draft.resolution_note.trim() || null,
      },
    });
  }

  const queueColumns = useMemo((): HubDataTableColumn<SafetyReportRow>[] => {
    return [
      {
        id: "report",
        header: "Report",
        sortKey: "id",
        sortValue: (row) => Number.parseInt(row.id, 10) || 0,
        cell: (row) => (
          <div className="space-y-0.5">
            <div className="font-mono text-xs font-semibold text-slate-900">#{row.id}</div>
            <div className="font-medium text-slate-900">{row.title}</div>
          </div>
        ),
      },
      {
        id: "kind",
        header: "Type",
        sortKey: "kind",
        sortValue: (row) => row.report_kind,
        cell: (row) => (
          <Badge tone={safetyReportKindTone(row.report_kind)}>
            {SAFETY_REPORT_KIND_LABELS[row.report_kind]}
          </Badge>
        ),
      },
      {
        id: "severity",
        header: "Severity",
        sortKey: "severity",
        sortValue: (row) => row.severity,
        cell: (row) => (
          <Badge tone={safetyReportSeverityTone(row.severity)}>
            {SAFETY_REPORT_SEVERITY_LABELS[row.severity]}
          </Badge>
        ),
      },
      {
        id: "area",
        header: "Area",
        sortKey: "area",
        sortValue: (row) => row.work_area,
        cell: (row) => (
          <div className="text-xs font-medium text-slate-900">{row.work_area}</div>
        ),
      },
      {
        id: "status",
        header: "Status",
        sortKey: "status",
        sortValue: (row) => row.status,
        cell: (row) => (
          <div className="flex flex-wrap gap-1">
            <Badge tone={safetyReportStatusTone(row.status)}>
              {SAFETY_REPORT_STATUS_LABELS[row.status]}
            </Badge>
            {row.pending_feedback ? <Badge tone="warning">Pending response</Badge> : null}
            {row.osha_recordable ? <Badge tone="danger">OSHA recordable</Badge> : null}
          </div>
        ),
      },
      {
        id: "assignee",
        header: "Assignee",
        sortKey: "assignee",
        sortValue: (row) => row.assigned_to_display_name ?? "",
        cell: (row) => (
          <Badge tone={row.assigned_to_display_name ? "neutral" : "warning"}>
            {row.assigned_to_display_name?.trim() || "Unassigned"}
          </Badge>
        ),
      },
      {
        id: "submitted",
        header: "Submitted",
        sortKey: "createdAt",
        sortValue: (row) => row.created_at,
        cell: (row) => (
          <div className="text-xs text-slate-600">
            <div>{row.reported_by_display_name ?? "Unknown"}</div>
            <div className="text-slate-500">{formatTimestamp(row.created_at)}</div>
          </div>
        ),
      },
      {
        id: "presence",
        header: "",
        sortValue: () => "",
        cell: (row) => (
          <QueueItemPresenceAvatars
            people={viewersForEntity(row.id)}
            className="ml-auto shrink-0"
          />
        ),
      },
    ];
  }, [viewersForEntity]);

  const filtersActive =
    statusScope !== "open" ||
    selectedKinds.length > 0 ||
    selectedSeverities.length > 0 ||
    workAreaFilter !== "" ||
    pendingFeedbackOnly ||
    oshaRecordableOnly ||
    debouncedSearch !== "";

  const queueError =
    listQuery.error instanceof ApiError
      ? listQuery.error.message
      : listQuery.error instanceof Error
        ? listQuery.error.message
        : null;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Safety reports</PageTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="gap-1.5"
            disabled={rows.length === 0}
            onClick={() => downloadSafetyReportsCsv(rows)}
          >
            <Download className="h-4 w-4" aria-hidden />
            Download CSV
          </Button>
          <Link to="/manufacturing-management/settings">
            <Button type="button" size="sm" variant="secondary" className="gap-1.5">
              <Settings className="h-4 w-4" aria-hidden />
              Notification list
            </Button>
          </Link>
          <Link to="/personal/safety-reports/new">
            <Button type="button" size="sm" className="gap-1.5">
              <Plus className="h-4 w-4" aria-hidden />
              New safety report
            </Button>
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Card className="p-3">
          <div className="text-xs font-medium text-slate-500">Open reports</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">
            {summaryCounts.openCount}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs font-medium text-slate-500">Open incidents / injuries</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-rose-700">
            {summaryCounts.incidentCount}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs font-medium text-slate-500">High / critical open</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-amber-700">
            {summaryCounts.highCriticalOpenCount}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs font-medium text-slate-500">Pending response</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">
            {summaryCounts.pendingFeedbackCount}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs font-medium text-slate-500">OSHA recordable</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">
            {summaryCounts.oshaRecordableCount}
          </div>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1.35fr)]">
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={statusScope === "open" ? "primary" : "secondary"}
                  onClick={() => setStatusScope("open")}
                >
                  Open
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={statusScope === "all" ? "primary" : "secondary"}
                  onClick={() => setStatusScope("all")}
                >
                  All
                </Button>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={pendingFeedbackOnly}
                    onChange={(event) => setPendingFeedbackOnly(event.target.checked)}
                  />
                  Pending response
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={oshaRecordableOnly}
                    onChange={(event) => setOshaRecordableOnly(event.target.checked)}
                  />
                  OSHA recordable
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="safety-report-search">Search safety reports</Label>
                  <Input
                    id="safety-report-search"
                    type="search"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Filter by report #, title, work area, reporter, equipment..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="safety-report-work-area-filter">Work area</Label>
                  <div className="mt-1">
                    <HubTouchSelect
                      id="safety-report-work-area-filter"
                      value={workAreaFilter}
                      onChange={(nextValue) => setWorkAreaFilter(nextValue)}
                      options={[
                        { value: "", label: "All work areas" },
                        ...SAFETY_WORK_AREA_OPTIONS,
                      ]}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-xs font-medium text-slate-600">Type:</span>
                  {SAFETY_REPORT_KINDS.map((kind) => (
                    <FilterChip
                      key={kind}
                      pressed={selectedKinds.includes(kind)}
                      onClick={() => toggleKindChip(kind)}
                    >
                      {SAFETY_REPORT_KIND_LABELS[kind]}
                    </FilterChip>
                  ))}
                  {selectedKinds.length > 0 ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedKinds([])}
                    >
                      Clear type filters
                    </Button>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-xs font-medium text-slate-600">Severity:</span>
                  {SAFETY_REPORT_SEVERITIES.map((severity) => (
                    <FilterChip
                      key={severity}
                      pressed={selectedSeverities.includes(severity)}
                      onClick={() => toggleSeverityChip(severity)}
                    >
                      {SAFETY_REPORT_SEVERITY_LABELS[severity]}
                    </FilterChip>
                  ))}
                  {selectedSeverities.length > 0 ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedSeverities([])}
                    >
                      Clear severity filters
                    </Button>
                  ) : null}
                </div>
              </div>
            </CardBody>
          </Card>

          <HubDataTable
            title={
              listQuery.isLoading
                ? "Loading safety reports..."
                : `${rows.length} safety report${rows.length === 1 ? "" : "s"}`
            }
            columns={queueColumns}
            rows={rows}
            rowKey={(row) => row.id}
            fillViewport={false}
            loading={listQuery.isLoading}
            loadingMessage="Loading safety reports..."
            error={queueError}
            empty="No safety reports have been submitted yet."
            isFiltered={filtersActive}
            filteredEmpty="No safety reports match these filters."
            onRowClick={selectRow}
            rowClassName={(row) => (selectedId === row.id ? "bg-brand-50" : undefined)}
          />
        </div>

        <Card>
          <CardHeader className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>
              {selected != null ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span className="font-mono">#{selected.id}</span>
                  <span>·</span>
                  <span>{selected.title}</span>
                </span>
              ) : (
                "Select a safety report"
              )}
            </CardTitle>

            {selected != null ? (
              canManage ? (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    id="safety-report-assignee"
                    aria-label="Assignee"
                    className="h-8 max-w-[12rem] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    value={selected.assigned_to_user_id ?? ""}
                    disabled={patchMutation.isPending || assigneeOptionsQuery.isLoading}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      patchMutation.mutate({
                        reportId: selected.id,
                        body: {
                          assigned_to_user_id: nextValue === "" ? null : nextValue,
                        },
                      });
                    }}
                  >
                    <option value="">Unassigned</option>
                    {assigneeOptions.map((option) => (
                      <option key={option.userId} value={option.userId}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={
                      patchMutation.isPending ||
                      currentUserId === "" ||
                      isAssignedToCurrentUser
                    }
                    onClick={() =>
                      patchMutation.mutate({
                        reportId: selected.id,
                        body: { assigned_to_user_id: currentUserId },
                      })
                    }
                  >
                    Take over
                  </Button>
                </div>
              ) : (
                <span className="text-sm text-slate-600">
                  {selected.assigned_to_display_name?.trim() || "Unassigned"}
                </span>
              )
            ) : null}
          </CardHeader>

          <CardBody className="space-y-5 border-t border-slate-100">
            {selectedId === "" ? (
              <p className="text-sm text-slate-600">
                Choose a safety report from the queue to inspect details and manage corrective actions.
              </p>
            ) : selected == null && detailQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading safety report...</p>
            ) : selected == null ? (
              <p className="text-sm text-rose-700">Safety report not found.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={safetyReportKindTone(selected.report_kind)}>
                    {SAFETY_REPORT_KIND_LABELS[selected.report_kind]}
                  </Badge>
                  <Badge tone={safetyReportSeverityTone(selected.severity)}>
                    Severity: {SAFETY_REPORT_SEVERITY_LABELS[selected.severity]}
                  </Badge>
                  <Badge tone={safetyReportStatusTone(selected.status)}>
                    {SAFETY_REPORT_STATUS_LABELS[selected.status]}
                  </Badge>
                  {selected.pending_feedback ? (
                    <Badge tone="warning">Pending reporter response</Badge>
                  ) : null}
                  {selected.osha_recordable ? (
                    <Badge tone="danger">OSHA recordable</Badge>
                  ) : null}
                </div>

                <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                  <p>
                    <span className="font-medium text-slate-900">Reported by:</span>{" "}
                    {selected.reported_by_display_name ?? EM_DASH}
                  </p>
                  <p>
                    <span className="font-medium text-slate-900">Submitted:</span>{" "}
                    {formatTimestamp(selected.created_at)}
                  </p>
                  <p>
                    <span className="font-medium text-slate-900">Work area:</span>{" "}
                    {selected.work_area}
                  </p>
                  <p>
                    <span className="font-medium text-slate-900">Occurred at:</span>{" "}
                    {formatTimestamp(selected.occurred_at)}
                  </p>
                  <p>
                    <span className="font-medium text-slate-900">Category:</span>{" "}
                    {SAFETY_REPORT_CATEGORY_LABELS[selected.category]}
                  </p>
                  {selected.report_kind === "incident" ||
                  selected.medical_attention_level !== "none" ? (
                    <p>
                      <span className="font-medium text-slate-900">Medical attention:</span>{" "}
                      {SAFETY_MEDICAL_ATTENTION_LABELS[selected.medical_attention_level]}
                    </p>
                  ) : null}
                  {selected.injured_person_name?.trim() ? (
                    <p>
                      <span className="font-medium text-slate-900">Injured / involved person:</span>{" "}
                      {selected.injured_person_name}
                    </p>
                  ) : null}
                  {selected.equipment_involved?.trim() ? (
                    <p>
                      <span className="font-medium text-slate-900">Equipment / tool:</span>{" "}
                      {selected.equipment_involved}
                    </p>
                  ) : null}
                  {selected.witnesses?.trim() ? (
                    <p className="sm:col-span-2">
                      <span className="font-medium text-slate-900">Witnesses:</span>{" "}
                      {selected.witnesses}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Reporter description
                    </div>
                    <p className="mt-1 whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900">
                      {selected.description}
                    </p>
                  </div>
                  {selected.immediate_action_taken?.trim() ? (
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Immediate action reported
                      </div>
                      <p className="mt-1 whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-2.5 text-sm text-slate-800">
                        {selected.immediate_action_taken}
                      </p>
                    </div>
                  ) : null}
                  {selected.suggested_prevention?.trim() ? (
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Reporter suggested prevention
                      </div>
                      <p className="mt-1 whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-2.5 text-sm text-slate-800">
                        {selected.suggested_prevention}
                      </p>
                    </div>
                  ) : null}
                </div>

                {draft != null ? (
                  <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-slate-900">
                        Investigation and corrective action
                      </h3>
                      {selected.resolved_at ? (
                        <span className="text-xs text-slate-500">
                          Resolved {formatTimestamp(selected.resolved_at)}
                          {selected.resolved_by_display_name
                            ? ` by ${selected.resolved_by_display_name}`
                            : ""}
                        </span>
                      ) : null}
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="safety-manage-status">Status</Label>
                        <div className="mt-1">
                          <HubTouchSelect
                            id="safety-manage-status"
                            value={draft.status}
                            disabled={!canManage || patchMutation.isPending}
                            onChange={(nextValue) =>
                              setDraft((current) =>
                                current
                                  ? { ...current, status: nextValue as SafetyReportStatus }
                                  : current,
                              )
                            }
                            options={SAFETY_REPORT_STATUSES.map((value) => ({
                              value,
                              label: SAFETY_REPORT_STATUS_LABELS[value],
                            }))}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="safety-manage-severity">Severity</Label>
                        <div className="mt-1">
                          <HubTouchSelect
                            id="safety-manage-severity"
                            value={draft.severity}
                            disabled={!canManage || patchMutation.isPending}
                            onChange={(nextValue) =>
                              setDraft((current) =>
                                current
                                  ? { ...current, severity: nextValue as SafetyReportSeverity }
                                  : current,
                              )
                            }
                            options={SAFETY_REPORT_SEVERITIES.map((value) => ({
                              value,
                              label: SAFETY_REPORT_SEVERITY_LABELS[value],
                            }))}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="safety-manage-category">Category</Label>
                        <div className="mt-1">
                          <HubTouchSelect
                            id="safety-manage-category"
                            value={draft.category}
                            disabled={!canManage || patchMutation.isPending}
                            onChange={(nextValue) =>
                              setDraft((current) =>
                                current
                                  ? { ...current, category: nextValue as SafetyReportCategory }
                                  : current,
                              )
                            }
                            options={SAFETY_REPORT_CATEGORIES.map((value) => ({
                              value,
                              label: SAFETY_REPORT_CATEGORY_LABELS[value],
                            }))}
                          />
                        </div>
                      </div>

                      {selected.report_kind === "incident" ||
                      draft.medical_attention_level !== "none" ||
                      draft.osha_recordable ? (
                        <div>
                          <Label htmlFor="safety-manage-medical">Medical attention</Label>
                          <div className="mt-1">
                            <HubTouchSelect
                              id="safety-manage-medical"
                              value={draft.medical_attention_level}
                              disabled={!canManage || patchMutation.isPending}
                              onChange={(nextValue) =>
                                setDraft((current) =>
                                  current
                                    ? {
                                        ...current,
                                        medical_attention_level:
                                          nextValue as SafetyMedicalAttentionLevel,
                                      }
                                    : current,
                                )
                              }
                              options={SAFETY_MEDICAL_ATTENTION_LEVELS.map((value) => ({
                                value,
                                label: SAFETY_MEDICAL_ATTENTION_LABELS[value],
                              }))}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap gap-4">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={draft.pending_feedback}
                          disabled={
                            !canManage ||
                            patchMutation.isPending ||
                            safetyReportStatusIsTerminal(draft.status)
                          }
                          onChange={(event) =>
                            setDraft((current) =>
                              current
                                ? { ...current, pending_feedback: event.target.checked }
                                : current,
                            )
                          }
                        />
                        Pending reporter response
                      </label>
                      {selected.report_kind === "incident" ||
                      draft.medical_attention_level !== "none" ||
                      draft.osha_recordable ? (
                        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={draft.osha_recordable}
                            disabled={!canManage || patchMutation.isPending}
                            onChange={(event) =>
                              setDraft((current) =>
                                current
                                  ? { ...current, osha_recordable: event.target.checked }
                                  : current,
                              )
                            }
                          />
                          OSHA recordable incident
                        </label>
                      ) : null}
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label htmlFor="safety-manage-containment">Containment action</Label>
                        {canManage ? (
                          <VoiceToTextMicButton
                            value={draft.containment_action}
                            ariaLabel="Dictate containment action"
                            onValueChange={(nextValue) =>
                              setDraft((current) =>
                                current ? { ...current, containment_action: nextValue } : current,
                              )
                            }
                          />
                        ) : null}
                      </div>
                      <TextArea
                        id="safety-manage-containment"
                        rows={2}
                        disabled={!canManage || patchMutation.isPending}
                        value={draft.containment_action}
                        onChange={(event) =>
                          setDraft((current) =>
                            current
                              ? { ...current, containment_action: event.target.value }
                              : current,
                          )
                        }
                        className="mt-1 bg-white"
                        placeholder="Immediate containment put in place by supervisors or safety team..."
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label htmlFor="safety-manage-root-cause">Root cause</Label>
                        {canManage ? (
                          <VoiceToTextMicButton
                            value={draft.root_cause}
                            ariaLabel="Dictate root cause"
                            onValueChange={(nextValue) =>
                              setDraft((current) =>
                                current ? { ...current, root_cause: nextValue } : current,
                              )
                            }
                          />
                        ) : null}
                      </div>
                      <TextArea
                        id="safety-manage-root-cause"
                        rows={2}
                        disabled={!canManage || patchMutation.isPending}
                        value={draft.root_cause}
                        onChange={(event) =>
                          setDraft((current) =>
                            current ? { ...current, root_cause: event.target.value } : current,
                          )
                        }
                        className="mt-1 bg-white"
                        placeholder="Why did this hazard or incident occur?"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label htmlFor="safety-manage-corrective">
                          Corrective and preventive action (visible to reporter)
                        </Label>
                        {canManage ? (
                          <VoiceToTextMicButton
                            value={draft.corrective_action}
                            ariaLabel="Dictate corrective action"
                            onValueChange={(nextValue) =>
                              setDraft((current) =>
                                current ? { ...current, corrective_action: nextValue } : current,
                              )
                            }
                          />
                        ) : null}
                      </div>
                      <TextArea
                        id="safety-manage-corrective"
                        rows={2}
                        disabled={!canManage || patchMutation.isPending}
                        value={draft.corrective_action}
                        onChange={(event) =>
                          setDraft((current) =>
                            current
                              ? { ...current, corrective_action: event.target.value }
                              : current,
                          )
                        }
                        className="mt-1 bg-white"
                        placeholder="Permanent engineering, process, or training fix to prevent recurrence..."
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label htmlFor="safety-manage-resolution">
                          Resolution summary (visible to reporter)
                        </Label>
                        {canManage ? (
                          <VoiceToTextMicButton
                            value={draft.resolution_note}
                            ariaLabel="Dictate resolution summary"
                            onValueChange={(nextValue) =>
                              setDraft((current) =>
                                current ? { ...current, resolution_note: nextValue } : current,
                              )
                            }
                          />
                        ) : null}
                      </div>
                      <TextArea
                        id="safety-manage-resolution"
                        rows={2}
                        disabled={!canManage || patchMutation.isPending}
                        value={draft.resolution_note}
                        onChange={(event) =>
                          setDraft((current) =>
                            current ? { ...current, resolution_note: event.target.value } : current,
                          )
                        }
                        className="mt-1 bg-white"
                        placeholder="Closing summary shared with the reporter when resolved..."
                      />
                    </div>

                    {canManage ? (
                      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3">
                        <div className="flex flex-wrap gap-2">
                          {selected.status === "open" ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={patchMutation.isPending}
                              onClick={() => saveDraftChanges("investigating")}
                            >
                              Mark investigating
                            </Button>
                          ) : null}
                          {selected.status === "open" || selected.status === "investigating" ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={patchMutation.isPending}
                              onClick={() => saveDraftChanges("corrective_action")}
                            >
                              Start corrective action
                            </Button>
                          ) : null}
                          {!safetyReportStatusIsTerminal(selected.status) ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="success"
                              disabled={patchMutation.isPending}
                              onClick={() => saveDraftChanges("resolved")}
                            >
                              Resolve report
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={patchMutation.isPending}
                              onClick={() => saveDraftChanges("investigating")}
                            >
                              Reopen
                            </Button>
                          )}
                        </div>
                        <SaveChangesButton
                          type="button"
                          size="sm"
                          isDirty={isDirty}
                          disabled={patchMutation.isPending}
                          onClick={() => saveDraftChanges()}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                      <MessageSquare className="h-4 w-4" aria-hidden />
                      Messages with reporter
                    </h3>
                    {canManage && !safetyReportStatusIsTerminal(selected.status) ? (
                      <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={requestFeedbackOnReply}
                          onChange={(event) => setRequestFeedbackOnReply(event.target.checked)}
                        />
                        Flag as waiting for reporter response
                      </label>
                    ) : null}
                  </div>
                  <HubSupportTicketMessageThread
                    messages={safetyReportMessagesAsHubThread(detailQuery.data?.messages ?? [])}
                    draft={replyBody}
                    onDraftChange={setReplyBody}
                    submitting={replyMutation.isPending}
                    canReply={canManage && !safetyReportStatusIsTerminal(selected.status)}
                    composeLabel="Message reporter"
                    onSubmit={() => {
                      const trimmed = replyBody.trim();
                      if (trimmed === "") return;
                      void replyMutation.mutateAsync({
                        body: trimmed,
                        requestFeedback: requestFeedbackOnReply,
                      });
                    }}
                  />
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
